import { useEffect, useMemo, useState } from 'react';
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  getYear,
  setMonth,
  setYear,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import clsx from 'clsx';

import CenteredOverlay from './CenteredOverlay';

type WeekStartsOn = 0 | 1 | 2 | 3 | 4 | 5 | 6;

interface Props {
  mode: 'month' | 'week';
  anchor: Date;
  weekStartsOn: WeekStartsOn;
  onPick: (date: Date) => void;
  onClose: () => void;
}

type Option = {
  key: string;
  date: Date;
  label: string;
  sub?: string;
};

// One unique letter per month. First-letter where unambiguous; the four
// duplicates (May/Jun/Jul/Aug) use a distinctive later letter: Y for maY,
// U for jUne, L for juLy, G for auGust.
const MONTH_LETTERS = ['j', 'f', 'm', 'a', 'y', 'u', 'l', 'g', 's', 'o', 'n', 'd'];

export default function Navigator({ mode, anchor, weekStartsOn, onPick, onClose }: Props) {
  const initialYear = useMemo(() => getYear(anchor), [anchor]);
  const [year, setYearState] = useState(initialYear);

  const options: Option[] = useMemo(() => {
    if (mode === 'month') {
      const base = new Date();
      return Array.from({ length: 12 }, (_, i) => {
        const d = setMonth(setYear(base, year), i);
        return { key: `m-${i}`, date: d, label: format(d, 'MMM') };
      });
    }
    const monthStart = startOfMonth(anchor);
    const firstWeek = startOfWeek(monthStart, { weekStartsOn });
    const monthEnd = endOfMonth(anchor);
    const lastWeek = endOfWeek(monthEnd, { weekStartsOn });
    const weeks: Option[] = [];
    let cur = firstWeek;
    let n = 1;
    while (cur <= lastWeek) {
      const end = addDays(cur, 6);
      weeks.push({
        key: `w-${n}`,
        date: cur,
        label: `Week ${n}`,
        sub: `${format(cur, 'MMM d')} – ${format(end, 'MMM d')}`
      });
      cur = addDays(cur, 7);
      n++;
    }
    return weeks;
  }, [mode, anchor, year, weekStartsOn]);

  const currentYear = getYear(new Date());
  const anchorMonth = anchor.getMonth();
  const anchorYear = getYear(anchor);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (mode === 'month' && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        setYearState((y) => y + (e.key === 'ArrowRight' ? 1 : -1));
        return;
      }
      // Month mode: each month has a unique mnemonic letter — see MONTH_LETTERS.
      if (mode === 'month' && /^[a-z]$/i.test(e.key)) {
        const letter = e.key.toLowerCase();
        const idx = MONTH_LETTERS.indexOf(letter);
        if (idx < 0) return;
        e.preventDefault();
        onPick(options[idx].date);
        return;
      }
      // Week mode: 1–9 + 0 → pick options 1-10.
      if (mode === 'week' && e.key >= '1' && e.key <= '9') {
        const idx = parseInt(e.key, 10) - 1;
        if (idx < options.length) {
          e.preventDefault();
          onPick(options[idx].date);
        }
        return;
      }
      if (mode === 'week' && e.key === '0' && options.length >= 10) {
        e.preventDefault();
        onPick(options[9].date);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [options, mode, onPick]);

  return (
    <CenteredOverlay
      ariaLabel={mode === 'month' ? 'Jump to month' : 'Jump to week'}
      onClose={onClose}
      maxWidth={420}
    >
      <header className="flex items-center justify-between border-b border-[var(--border)] px-4 py-2">
          {mode === 'month' ? (
            <>
              <button
                type="button"
                aria-label="previous year"
                onClick={() => setYearState((y) => y - 1)}
                className="rounded px-2 py-1 text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
              >
                ‹
              </button>
              <div className="text-sm font-semibold">{year}</div>
              <button
                type="button"
                aria-label="next year"
                onClick={() => setYearState((y) => y + 1)}
                className="rounded px-2 py-1 text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
              >
                ›
              </button>
            </>
          ) : (
            <>
              <div className="text-sm font-semibold">{format(anchor, 'MMMM yyyy')}</div>
              <div className="text-[0.72rem] text-[var(--text-faint)]">
                press 1–{options.length} to jump
              </div>
            </>
          )}
        </header>
        <div
          className={clsx(
            'gap-2 p-3',
            mode === 'month' ? 'grid grid-cols-4' : 'flex flex-col'
          )}
        >
          {options.map((o, i) => {
            const isCurrent =
              mode === 'month'
                ? year === anchorYear && i === anchorMonth
                : false;
            const isThisMonth =
              mode === 'month' && year === currentYear && i === new Date().getMonth();
            const hotkey =
              mode === 'month'
                ? MONTH_LETTERS[i].toUpperCase()
                : i < 9
                  ? String(i + 1)
                  : i === 9
                    ? '0'
                    : null;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => onPick(o.date)}
                className={clsx(
                  'relative rounded border border-[var(--border)] px-3 py-2 text-left text-sm hover:bg-[var(--bg-hover)]',
                  isCurrent && 'bg-[var(--bg-muted)] font-semibold',
                  isThisMonth && !isCurrent && 'border-[var(--today-fg)]'
                )}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span>{o.label}</span>
                  {hotkey && (
                    <kbd className="rounded border border-[var(--border-strong)] bg-[var(--bg)] px-[5px] py-[1px] font-mono text-[0.65rem] text-[var(--text-faint)]">
                      {hotkey}
                    </kbd>
                  )}
                </div>
                {o.sub && (
                  <div className="text-[0.72rem] text-[var(--text-muted)]">{o.sub}</div>
                )}
              </button>
            );
          })}
        </div>
    </CenteredOverlay>
  );
}
